import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Role } from "@/constants/rolePermissions";
import { getStatusIcon } from "@/utils/shared/getStatusIcon";
import { getStatusText } from "@/utils/shared/getStatusText";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { RotateCcw, Settings } from "lucide-react";

interface ManagedUserCardProps {
  user: any;
  onAssign: (user: any) => void;
  onResendInvitation: (userId: string) => void;
  isLoading?: boolean;
  role?: Role;
}

const ManagedUserCard = ({
  user,
  onAssign,
  onResendInvitation,
  isLoading = false,
  role,
}: ManagedUserCardProps) => {
  const assignments = user?.user_property_assignments || [];

  return (
    <Card className="transition-shadow hover:shadow-md rounded-xl border border-gray-200">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {user.first_name} {user.last_name}
              </h3>
              <p className="text-sm text-gray-500">{user.user_id}</p>
            </div>

            <div className="text-sm text-gray-700">
              <p>
                <span className="font-medium">Teléfono:</span>{" "}
                {user.phone || (
                  <span className="text-gray-400">No especificado</span>
                )}
              </p>
            </div>

            {assignments.length > 0 && (
              <div>
                <p className="font-medium text-sm text-gray-700">
                  Propiedades asignadas:
                </p>

                <div className="mt-2 flex flex-wrap gap-2">
                  {assignments.map((assignment) => (
                    <span
                      key={assignment.property_id}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-brand-primary"
                    >
                      {assignment?.properties?.short_name ||
                        assignment?.property_id}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col justify-between space-y-4">
            <div className="space-y-2 text-sm text-gray-700">
              <div>
                <span className="font-medium">Propiedades:</span>{" "}
                {assignments.length}
              </div>

              <div>
                <span className="font-medium">Creado:</span>{" "}
                {format(new Date(user.created_at), "dd/MM/yy", {
                  locale: es,
                })}
              </div>

              <div className="flex items-center gap-2">
                {getStatusIcon(user.status)}
                <span className="font-medium">
                  {getStatusText(user.status)}
                </span>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAssign(user)}
              >
                <Settings className="w-4 h-4 mr-1" />
                Asignar
              </Button>

              {(user.status === "pending" || user.status === "expired") && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onResendInvitation(user.user_id)}
                  disabled={isLoading}
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reenviar
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ManagedUserCard;
